import { Box, Skeleton, Typography } from "@mui/material";
import {
  ResponsiveChartContainer,
  AreaPlot,
  LinePlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsTooltip,
  ChartsLegend,
} from "@mui/x-charts";
import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  DEMO_MODE_KEY,
  SEQUENCE_ACTION_TYPE_EMAIL,
  SEQUENCE_ACTION_TYPE_SMS,
  SEQUENCE_ACTION_TYPE_VOICE_MESSAGE,
  SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT,
  SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST,
  SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE,
  SEQUENCE_ACTION_TYPE_BROWSER_TASK,
  SEQUENCE_ACTION_TYPE_HTTP_REQUEST,
  SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN,
  SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION,
  SEQUENCE_ACTION_TYPE_THANKSIO,
  SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE,
  SEQUENCE_ACTION_TYPE_ADD_TO_LIST,
  SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN,
  SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN,
} from "../constants";
import { generateDemoActivityTimelineData } from "../constants/demoData";
import { hostname } from "../utils";

export interface HourlyDataItem {
  hour: string;
  count: number;
  actionType?: string;
}

export interface TimelineEvents {
  opens?: { hour: string; count: number }[];
  clicks?: { hour: string; count: number }[];
  bounces?: { hour: string; count: number }[];
  scheduleViews?: { hour: string; count: number }[];
  bookings?: { hour: string; count: number }[];
  replies?: { hour: string; count: number }[];
}

export interface ActivityTimelineData {
  hourlyData?: HourlyDataItem[];
  timelineEvents?: TimelineEvents;
}

interface ActivityTimelineProps {
  sequenceId?: string | null;
  companyId: string;
  getToken: () => Promise<string>;
  title?: string;
  showTitle?: boolean;
  height?: number;
  daysToShow?: number;
  emptyMessage?: string;
  onError?: (error: string) => void;
  enabled?: boolean;
}

const getActionTypeLabel = (actionType: string) => {
  // Handle auto_agent combined types: "auto_agent:email", "auto_agent:sms", "auto_agent:voice"
  if (actionType.startsWith("auto_agent")) {
    if (actionType === "auto_agent") {
      return "Auto Agent"; // Legacy executions without channel info
    }
    const channel = actionType.split(":")[1];
    if (!channel) {
      return "Auto Agent"; // Malformed or missing channel
    }
    switch (channel) {
      case "email":
        return "Auto Agent (Email)";
      case "sms":
        return "Auto Agent (SMS)";
      case "voice":
        return "Auto Agent (Voice)";
      default:
        return "Auto Agent";
    }
  }

  switch (actionType) {
    case SEQUENCE_ACTION_TYPE_EMAIL:
      return "Emails";
    case SEQUENCE_ACTION_TYPE_SMS:
      return "SMS Messages";
    case SEQUENCE_ACTION_TYPE_VOICE_MESSAGE:
      return "Voice Messages";
    case SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION:
      return "Agent Conversations";
    case SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT:
      return "LinkedIn Profile Visits";
    case SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST:
      return "LinkedIn Connection Requests";
    case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
      return "LinkedIn Messages";
    case SEQUENCE_ACTION_TYPE_BROWSER_TASK:
      return "Browser Tasks";
    case SEQUENCE_ACTION_TYPE_HTTP_REQUEST:
      return "HTTP Requests";
    case SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN:
      return "Prosp Campaigns";
    case SEQUENCE_ACTION_TYPE_THANKSIO:
      return "Thanks.io Direct Mail";
    case SEQUENCE_ACTION_TYPE_HEYREACH_CAMPAIGN:
      return "HeyReach Campaigns";
    case SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN:
      return "Salesforge Campaigns";
    case SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE:
      return "Add to Sequence";
    case SEQUENCE_ACTION_TYPE_ADD_TO_LIST:
      return "Add to List";
    default:
      return actionType;
  }
};

const getActionTypeColor = (actionType: string) => {
  // Handle auto_agent combined types: "auto_agent:email", "auto_agent:sms", "auto_agent:voice"
  if (actionType.startsWith("auto_agent")) {
    if (actionType === "auto_agent") {
      return "#9c27b0"; // Purple for legacy executions
    }
    const channel = actionType.split(":")[1];
    if (!channel) {
      return "#9c27b0"; // Purple for malformed
    }
    switch (channel) {
      case "email":
        return "#1976d2"; // Darker Blue (distinct from regular email)
      case "sms":
        return "#388e3c"; // Darker Green (distinct from regular SMS)
      case "voice":
        return "#f57c00"; // Darker Orange (distinct from regular voice)
      default:
        return "#9c27b0"; // Purple
    }
  }

  switch (actionType) {
    case SEQUENCE_ACTION_TYPE_EMAIL:
      return "#2196f3"; // Blue
    case SEQUENCE_ACTION_TYPE_SMS:
      return "#4caf50"; // Green
    case SEQUENCE_ACTION_TYPE_VOICE_MESSAGE:
      return "#ffc107"; // Yellow/Orange
    case SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION:
      return "#9c27b0"; // Purple (distinct from other purples)
    case SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT:
      return "#26a69a"; // Teal
    case SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST:
      return "#7c4dff"; // Purple
    case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
      return "#66bb6a"; // Light Green
    case SEQUENCE_ACTION_TYPE_BROWSER_TASK:
      return "#29b6f6"; // Light Blue
    case SEQUENCE_ACTION_TYPE_HTTP_REQUEST:
      return "#ff7043"; // Deep Orange
    case SEQUENCE_ACTION_TYPE_PROSP_CAMPAIGN:
      return "#ab47bc"; // Purple
    case SEQUENCE_ACTION_TYPE_SALESFORGE_CAMPAIGN:
      return "#5e35b1"; // Deep Purple
    case SEQUENCE_ACTION_TYPE_THANKSIO:
      return "#ec407a"; // Pink
    case SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE:
      return "#546e7a"; // Blue Grey
    case SEQUENCE_ACTION_TYPE_ADD_TO_LIST:
      return "#00897b"; // Teal
    default:
      return "#78909c"; // Blue Grey
  }
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  sequenceId,
  companyId,
  getToken,
  title,
  showTitle = true,
  height = 300,
  daysToShow = 3,
  emptyMessage,
  onError,
  enabled = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ActivityTimelineData | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(
    () => localStorage.getItem(DEMO_MODE_KEY) === "true"
  );

  // Listen for demo mode changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEMO_MODE_KEY) {
        setIsDemoMode(e.newValue === "true");
      }
    };

    const checkDemoMode = () => {
      const currentValue = localStorage.getItem(DEMO_MODE_KEY) === "true";
      setIsDemoMode((prev) => (prev !== currentValue ? currentValue : prev));
    };

    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(checkDemoMode, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const defaultTitle = sequenceId
    ? `Activity Timeline (Last ${daysToShow} Days)`
    : `All Sequences Activity (Last ${daysToShow} Days)`;

  const defaultEmptyMessage = sequenceId
    ? `No activity data available for the last ${daysToShow} days`
    : `No sequence activity in the last ${daysToShow} days`;

  useEffect(() => {
    const fetchData = async () => {
      if (isDemoMode) {
        setLoading(true);
        setTimeout(() => {
          setData(generateDemoActivityTimelineData());
          setLoading(false);
        }, 300);
        return;
      }

      setLoading(true);
      try {
        const token = await getToken();
        const url = sequenceId
          ? `${hostname}/sequences/${sequenceId}/hourly-metrics`
          : `${hostname}/sequences/hourly-metrics`;

        const response = await axios.get(url, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });

        setData(response.data);
      } catch (err: any) {
        console.error("Error fetching activity timeline data:", err);
        if (onError) {
          onError(err.response?.data?.error || "Failed to load activity data");
        }
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    if (companyId && enabled) {
      fetchData();
    }
  }, [sequenceId, companyId, getToken, enabled, isDemoMode]);

  const getChartData = () => {
    if (!data) return null;

    const hasHourlyData = data.hourlyData && data.hourlyData.length > 0;
    const hasTimelineEvents =
      data.timelineEvents &&
      (data.timelineEvents.opens?.length ||
        data.timelineEvents.clicks?.length ||
        data.timelineEvents.bounces?.length ||
        data.timelineEvents.scheduleViews?.length ||
        data.timelineEvents.bookings?.length ||
        data.timelineEvents.replies?.length);

    if (!hasHourlyData && !hasTimelineEvents) return null;

    // Generate all hours for the specified time range
    const hours: string[] = [];
    const now = new Date();
    const startDate = new Date(
      now.getTime() - daysToShow * 24 * 60 * 60 * 1000
    );

    // Round to the start of the hour
    startDate.setMinutes(0, 0, 0);

    for (let d = new Date(startDate); d <= now; d.setHours(d.getHours() + 1)) {
      hours.push(new Date(d).toISOString());
    }

    // Group data by action type for bar series
    const dataByType: { [key: string]: { [hour: string]: number } } =
      Object.create(null);
    if (hasHourlyData && data.hourlyData) {
      data.hourlyData.forEach((item) => {
        const actionType = item.actionType || "unknown";
        if (!Object.prototype.hasOwnProperty.call(dataByType, actionType)) {
          dataByType[actionType] = Object.create(null);
        }
        dataByType[actionType][item.hour] = item.count;
      });
    }

    // Create area series for each action type
    const areaSeries = Object.keys(dataByType).map((actionType) => {
      const seriesData = hours.map((hour) => {
        const hourKey = Object.keys(dataByType[actionType]).find(
          (h) => new Date(h).toISOString().slice(0, 13) === hour.slice(0, 13)
        );
        const value = hourKey
          ? parseInt(String(dataByType[actionType][hourKey]))
          : 0;
        return value;
      });

      return {
        label: getActionTypeLabel(actionType),
        data: seriesData,
        color: getActionTypeColor(actionType),
        stack: "total",
        type: "line" as const,
        area: true,
        showMark: false,
      };
    });

    // Helper to convert timeline events to series data
    const timelineToSeriesData = (
      events: { hour: string; count: number }[] | undefined
    ) => {
      if (!events || events.length === 0) return null;

      const eventsByHour: { [hour: string]: number } = {};
      events.forEach((e) => {
        if (e.hour) {
          eventsByHour[e.hour] = e.count;
        }
      });

      return hours.map((hour) => {
        const hourKey = Object.keys(eventsByHour).find(
          (h) => new Date(h).toISOString().slice(0, 13) === hour.slice(0, 13)
        );
        return hourKey ? eventsByHour[hourKey] : 0;
      });
    };

    // Create area series for timeline events
    const timelineAreaSeries: any[] = [];

    if (data.timelineEvents) {
      const opensData = timelineToSeriesData(data.timelineEvents.opens);
      if (opensData && opensData.some((v: number) => v > 0)) {
        timelineAreaSeries.push({
          label: "Opens",
          data: opensData,
          color: "#4caf50", // Green
          type: "line" as const,
          area: true,
          showMark: false,
          stack: "total",
        });
      }

      const clicksData = timelineToSeriesData(data.timelineEvents.clicks);
      if (clicksData && clicksData.some((v: number) => v > 0)) {
        timelineAreaSeries.push({
          label: "Clicks",
          data: clicksData,
          color: "#ffc107", // Yellow/Orange
          type: "line" as const,
          area: true,
          showMark: false,
          stack: "total",
        });
      }

      const bouncesData = timelineToSeriesData(data.timelineEvents.bounces);
      if (bouncesData && bouncesData.some((v: number) => v > 0)) {
        timelineAreaSeries.push({
          label: "Bounces",
          data: bouncesData,
          color: "#ef5350", // Red
          type: "line" as const,
          area: true,
          showMark: false,
          stack: "total",
        });
      }

      const scheduleViewsData = timelineToSeriesData(
        data.timelineEvents.scheduleViews
      );
      if (scheduleViewsData && scheduleViewsData.some((v: number) => v > 0)) {
        timelineAreaSeries.push({
          label: "Meeting Views",
          data: scheduleViewsData,
          color: "#7c4dff", // Purple
          type: "line" as const,
          area: true,
          showMark: false,
          stack: "total",
        });
      }

      const bookingsData = timelineToSeriesData(data.timelineEvents.bookings);
      if (bookingsData && bookingsData.some((v: number) => v > 0)) {
        timelineAreaSeries.push({
          label: "Bookings",
          data: bookingsData,
          color: "#26a69a", // Teal
          type: "line" as const,
          area: true,
          showMark: false,
          stack: "total",
        });
      }

      const repliesData = timelineToSeriesData(data.timelineEvents.replies);
      if (repliesData && repliesData.some((v: number) => v > 0)) {
        timelineAreaSeries.push({
          label: "Replies",
          data: repliesData,
          color: "#ff9800", // Orange
          type: "line" as const,
          area: true,
          showMark: false,
          stack: "total",
        });
      }
    }

    // Combine area series - timeline events first (bottom), then action types (top)
    const series = [...timelineAreaSeries, ...areaSeries];

    // Format x-axis labels to show date and hour
    const xAxisData = hours.map((hour) => {
      const date = new Date(hour);
      return (
        date.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
        "\n" +
        date.toLocaleTimeString("en-US", { hour: "numeric", hour12: true })
      );
    });

    return {
      xAxis: [
        {
          scaleType: "point" as const,
          data: xAxisData,
          id: "x-axis",
        },
      ],
      series: series,
    };
  };

  if (loading) {
    return (
      <Box>
        {showTitle && (
          <Typography variant="h6" gutterBottom>
            {title || defaultTitle}
          </Typography>
        )}
        <Box sx={{ width: "100%", height }}>
          <Skeleton variant="rounded" width="100%" height={height} />
        </Box>
      </Box>
    );
  }

  const chartData = getChartData();

  if (!chartData) {
    return (
      <Box sx={{ mb: 4, p: 3, textAlign: "center", borderRadius: 1 }}>
        {showTitle && (
          <Typography variant="h6" gutterBottom>
            {title || defaultTitle}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {emptyMessage || defaultEmptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {showTitle && (
        <Typography variant="h6" gutterBottom>
          {title || defaultTitle}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>
        <ResponsiveChartContainer
          xAxis={chartData.xAxis}
          series={chartData.series as any}
          height={height}
          margin={{
            top: 50,
            right: 20,
            bottom: 60,
            left: 50,
          }}
          sx={{
            width: "100%",
            height: "100%",
            "& .MuiChartsAxis-tickLabel": {
              fontSize: "0.75rem",
              transform: "rotate(-45deg)",
              transformOrigin: "right",
              textAnchor: "end",
            },
            "& .MuiChartsLegend-label": {
              fontSize: "0.875rem",
            },
            "& .MuiAreaElement-root": {
              fillOpacity: 0.7,
            },
          }}
        >
          <AreaPlot />
          <LinePlot />
          <ChartsXAxis />
          <ChartsYAxis />
          <ChartsTooltip />
          <ChartsLegend
            direction="row"
            position={{ vertical: "top", horizontal: "middle" }}
          />
        </ResponsiveChartContainer>
      </Box>
    </Box>
  );
};

export default ActivityTimeline;
